import api from '../../../../../api/client';
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Layout,
  Input,
  Button,
  Avatar,
  List,
  Badge,
  Spin,
  message,
  Popover,
  Space,
  Typography,
  Divider,
  Tooltip,
  Upload,
} from "antd";
import {
  SendOutlined,
  SearchOutlined,
  PaperClipOutlined,
  SmileOutlined,
  CheckOutlined,
  CheckCircleOutlined,
  PhoneOutlined,
  VideoCameraOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import EmojiPicker from "emoji-picker-react";
import axios from "axios";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useSocket } from "../../../../../contexts/SocketContext";
import config from "../../../../../config";

dayjs.extend(relativeTime);

const { Sider, Content } = Layout;
const { Text, Title } = Typography;

const ChatContainer = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [userLastMessages, setUserLastMessages] = useState({});
  const [unreadCounts, setUnreadCounts] = useState({});
  const [messageStatuses, setMessageStatuses] = useState({});
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const { socket, isConnected } = useSocket();
  const environment = window.location.hostname === "localhost" ? "development" : "production";
  const apiUrl = config[environment].apiUrl;
  const token = localStorage.getItem("token");

  // Fetch all users
  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await api.get(`/auth`);
      const usersData = Array.isArray(data) ? data : data.users || [];
      const otherUsers = usersData.filter((u) => u.id !== currentUser?.id);
      setUsers(otherUsers);
    } catch (err) {
      message.error("Failed to load users");
    }
  }, [apiUrl, currentUser?.id]);

  // Fetch messages between current user and selected user
  const fetchMessages = useCallback(async (userId) => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/messages`, {
        params: { user1: currentUser?.id, user2: userId },
      });
      setMessages(data);
      
      // Mark unread messages as read
      const unreadMessages = data.filter(msg => msg.receiver_id === currentUser?.id && !msg.read_at);
      if (unreadMessages.length > 0 && socket) {
        unreadMessages.forEach(msg => {
          socket.emit("markAsRead", { messageId: msg.id });
        });
      }
    } catch (err) {
      message.error("Failed to load messages");
    } finally {
      setLoading(false);
    }
  }, [apiUrl, currentUser?.id, socket]);

  // Fetch last messages and unread counts
  const fetchLastMessages = useCallback(async () => {
    try {
      const { data } = await api.get(`/messages/last?userId=${currentUser?.id}`);
      setUserLastMessages(data.lastMessages || {});
      setUnreadCounts(data.unreadCounts || {});
    } catch (err) {
      console.error("Failed to load last messages");
    }
  }, [apiUrl, currentUser?.id]);

  useEffect(() => {
    fetchUsers();
    fetchLastMessages();
  }, [fetchUsers, fetchLastMessages]);

  useEffect(() => {
    if (selectedUser) {
      fetchMessages(selectedUser.id);
    }
  }, [selectedUser, fetchMessages]);

  // Socket event listeners
  useEffect(() => {
    if (!socket) return;

    // Handle receiving new message
    const handleReceiveMessage = (message) => {
      const isBetween = selectedUser && 
        ((message.sender_id === currentUser?.id && message.receiver_id === selectedUser.id) ||
         (message.sender_id === selectedUser.id && message.receiver_id === currentUser?.id));
      
      if (isBetween) {
        setMessages((prev) => [...prev, message]);
        // Mark as read immediately
        if (message.sender_id !== currentUser?.id) {
          socket.emit("markAsRead", { messageId: message.id });
        }
      }
      fetchLastMessages();
    };

    // Handle typing indicator
    const handleTyping = ({ senderId }) => {
      if (senderId === selectedUser?.id) setIsTyping(true);
    };

    const handleStopTyping = ({ senderId }) => {
      if (senderId === selectedUser?.id) setIsTyping(false);
    };

    // Handle message status (delivered)
    const handleMessageStatus = ({ messageId, status }) => {
      setMessageStatuses(prev => ({ ...prev, [messageId]: status }));
    };

    // Handle message read
    const handleMessageRead = ({ messageId }) => {
      setMessages(prev => prev.map(msg => 
        msg.id === messageId ? { ...msg, read_at: new Date() } : msg
      ));
      fetchLastMessages();
    };

    // Handle user offline
    const handleUserOffline = ({ userId }) => {
      if (userId === selectedUser?.id) {
        setUsers(prev => prev.map(u => 
          u.id === userId ? { ...u, online: false, last_seen: new Date() } : u
        ));
      }
      fetchLastMessages();
    };

    socket.on("receiveMessage", handleReceiveMessage);
    socket.on("typing", handleTyping);
    socket.on("stopTyping", handleStopTyping);
    socket.on("messageStatus", handleMessageStatus);
    socket.on("messageRead", handleMessageRead);
    socket.on("userOffline", handleUserOffline);

    return () => {
      socket.off("receiveMessage", handleReceiveMessage);
      socket.off("typing", handleTyping);
      socket.off("stopTyping", handleStopTyping);
      socket.off("messageStatus", handleMessageStatus);
      socket.off("messageRead", handleMessageRead);
      socket.off("userOffline", handleUserOffline);
    };
  }, [socket, selectedUser, currentUser?.id, fetchLastMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);

    if (socket && selectedUser) {
      socket.emit("typing", { receiverId: selectedUser.id });
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        socket.emit("stopTyping", { receiverId: selectedUser.id });
      }, 1000);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedUser || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/messages`, {
        sender_id: currentUser.id,
        receiver_id: selectedUser.id,
        content: newMessage.trim(),
      });
      
      setMessages((prev) => [...prev, data]);
      setNewMessage("");
      setShowEmojiPicker(false);
      
      // Emit via socket for real-time delivery
      if (socket) {
        socket.emit("sendMessage", {
          sender_id: currentUser.id,
          receiver_id: selectedUser.id,
          content: newMessage.trim(),
        });
      }
      fetchLastMessages();
    } catch (err) {
      message.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleEmojiClick = (emoji) => {
    setNewMessage((prev) => prev + emoji.emoji);
  };

  const filteredUsers = users.filter((user) =>
    `${user.firstname} ${user.lastname}`.toLowerCase().includes(searchText.toLowerCase())
  );

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const aMsg = userLastMessages[a.id];
    const bMsg = userLastMessages[b.id];
    const aTime = aMsg ? new Date(aMsg.created_at).getTime() : 0;
    const bTime = bMsg ? new Date(bMsg.created_at).getTime() : 0;
    return bTime - aTime;
  });

  const getLastMessagePreview = (userId) => {
    const msg = userLastMessages[userId];
    if (!msg) return "No messages yet";
    const sender = msg.sender_id === currentUser?.id ? "You: " : "";
    const content = msg.content?.length > 30 ? msg.content.substring(0, 30) + "..." : msg.content;
    return sender + content;
  };

  const getMessageStatus = (msg) => {
    if (msg.sender_id !== currentUser?.id) return null;
    if (msg.read_at) {
      return <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 12 }} />;
    }
    if (messageStatuses[msg.id] === "delivered") {
      return <CheckCircleOutlined style={{ color: "#8c8c8c", fontSize: 12 }} />;
    }
    return <CheckOutlined style={{ color: "#8c8c8c", fontSize: 12 }} />;
  };

  return (
    <Layout style={{ height: "calc(100vh - 120px)", background: "#fff", borderRadius: 12, overflow: "hidden" }}>
      {/* Users Sidebar */}
      <Sider width={320} style={{ background: "#f5f5f5", borderRight: "1px solid #e8e8e8" }}>
        <div style={{ padding: 16 }}>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search users..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
          />
        </div>
        <Divider style={{ margin: 0 }} />
        <div style={{ height: "calc(100vh - 200px)", overflowY: "auto" }}>
          <List
            dataSource={sortedUsers}
            renderItem={(user) => (
              <List.Item
                onClick={() => setSelectedUser(user)}
                style={{
                  cursor: "pointer",
                  padding: "12px 16px",
                  background: selectedUser?.id === user.id ? "#e6f7ff" : "transparent",
                  transition: "all 0.3s",
                }}
                hoverable
              >
                <List.Item.Meta
                  avatar={
                    <Badge dot={unreadCounts[user.id] > 0} color="#ff4d4f">
                      <Avatar src={user.passport ? `/uploads/${user.passport}` : null}>
                        {user.firstname?.[0]}
                      </Avatar>
                    </Badge>
                  }
                  title={
                    <Space>
                      <span>{user.firstname} {user.lastname}</span>
                      {unreadCounts[user.id] > 0 && (
                        <Badge count={unreadCounts[user.id]} style={{ backgroundColor: "#ff4d4f" }} />
                      )}
                    </Space>
                  }
                  description={
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {getLastMessagePreview(user.id)}
                    </Text>
                  }
                />
              </List.Item>
            )}
          />
        </div>
      </Sider>

      {/* Chat Area */}
      <Content>
        {selectedUser ? (
          <>
            {/* Chat Header */}
            <div
              style={{
                padding: "12px 24px",
                borderBottom: "1px solid #f0f0f0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Space>
                <Avatar src={selectedUser.passport ? `/uploads/${selectedUser.passport}` : null} size={40}>
                  {selectedUser.firstname?.[0]}
                </Avatar>
                <div>
                  <Title level={5} style={{ margin: 0 }}>
                    {selectedUser.firstname} {selectedUser.lastname}
                  </Title>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {isTyping ? (
                      <span style={{ color: "#52c41a" }}>Typing...</span>
                    ) : (
                      selectedUser.online ? "Online" : `Last seen ${dayjs(selectedUser.last_seen).fromNow()}`
                    )}
                  </Text>
                </div>
              </Space>
              <Space>
                <Tooltip title="Voice Call"><Button icon={<PhoneOutlined />} shape="circle" /></Tooltip>
                <Tooltip title="Video Call"><Button icon={<VideoCameraOutlined />} shape="circle" /></Tooltip>
                <Tooltip title="More"><Button icon={<MoreOutlined />} shape="circle" /></Tooltip>
              </Space>
            </div>

            {/* Messages Area */}
            <div
              style={{
                height: "calc(100vh - 280px)",
                overflowY: "auto",
                padding: "24px",
                background: "#fafafa",
              }}
            >
              {loading ? (
                <div style={{ textAlign: "center", padding: 40 }}>
                  <Spin />
                </div>
              ) : messages.length === 0 ? (
                <div style={{ textAlign: "center", padding: 40, color: "#999" }}>
                  No messages yet. Start the conversation!
                </div>
              ) : (
                messages.map((msg, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      justifyContent: msg.sender_id === currentUser?.id ? "flex-end" : "flex-start",
                      marginBottom: 16,
                    }}
                  >
                    <div
                      style={{
                        maxWidth: "60%",
                        padding: "10px 14px",
                        borderRadius: 12,
                        background: msg.sender_id === currentUser?.id ? "#1677ff" : "#fff",
                        color: msg.sender_id === currentUser?.id ? "#fff" : "#333",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                      }}
                    >
                      <div>{msg.content}</div>
                      <div
                        style={{
                          fontSize: 10,
                          marginTop: 4,
                          color: msg.sender_id === currentUser?.id ? "rgba(255,255,255,0.7)" : "#999",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          gap: 4,
                        }}
                      >
                        {dayjs(msg.created_at).format("HH:mm")}
                        {getMessageStatus(msg)}
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div
              style={{
                padding: "16px 24px",
                borderTop: "1px solid #f0f0f0",
                background: "#fff",
              }}
            >
              <Space.Compact style={{ width: "100%" }}>
                <Upload showUploadList={false} beforeUpload={() => false}>
                  <Button icon={<PaperClipOutlined />} />
                </Upload>
                <Popover
                  content={<EmojiPicker onEmojiClick={handleEmojiClick} />}
                  trigger="click"
                  open={showEmojiPicker}
                  onOpenChange={setShowEmojiPicker}
                  placement="top"
                >
                  <Button icon={<SmileOutlined />} />
                </Popover>
                <Input
                  value={newMessage}
                  onChange={handleInputChange}
                  onPressEnter={handleSendMessage}
                  placeholder="Type a message..."
                  style={{ flex: 1 }}
                />
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={handleSendMessage}
                  loading={sending}
                >
                  Send
                </Button>
              </Space.Compact>
            </div>
          </>
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              color: "#999",
            }}
          >
            Select a user to start chatting
          </div>
        )}
      </Content>
    </Layout>
  );
};

export default ChatContainer;